import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Agents } from '@air/api';
import { type ListAgentSessionsResponse } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import { getPrivateWorkspaceAgentSessionGetKey } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';
import {
  buildPrivateWorkspaceAgentSessionsListArchivedParams,
  buildPrivateWorkspaceAgentSessionsListInfiniteParams,
  getPrivateWorkspaceAgentSessionsListInfiniteKey,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionsListInfinite';
import { buildWorkspaceAgentSessionListCachePatch } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentSessionListCachePatch';
import {
  movePrivateWorkspaceAgentSessionToLiveListCache,
  removePrivateWorkspaceAgentSessionFromListCache,
  upsertPrivateWorkspaceAgentSessionInListCache,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/patchPrivateWorkspaceAgentSessionsListCache';

export type UsePrivateWorkspaceAgentSessionUnarchiveMutationParams = NonNullable<
  Parameters<typeof Agents.Sessions.unarchive>[0]
>;

type UnarchiveMutationContext = {
  liveListKey: ReturnType<typeof getPrivateWorkspaceAgentSessionsListInfiniteKey>;
  archivedListKey: ReturnType<typeof getPrivateWorkspaceAgentSessionsListInfiniteKey>;
  previousLiveList: InfiniteData<ListAgentSessionsResponse> | undefined;
  previousArchivedList: InfiniteData<ListAgentSessionsResponse> | undefined;
};

export const usePrivateWorkspaceAgentSessionUnarchiveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UsePrivateWorkspaceAgentSessionUnarchiveMutationParams) =>
      callAirApi(() => Agents.Sessions.unarchive(params)),
    onMutate: async (variables) => {
      const liveListParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(variables.workspaceId);
      const archivedListParams = buildPrivateWorkspaceAgentSessionsListArchivedParams(variables.workspaceId);
      const liveListKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(liveListParams);
      const archivedListKey = getPrivateWorkspaceAgentSessionsListInfiniteKey(archivedListParams);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: liveListKey }),
        queryClient.cancelQueries({ queryKey: archivedListKey }),
      ]);

      const previousLiveList = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(liveListKey);
      const previousArchivedList = queryClient.getQueryData<InfiniteData<ListAgentSessionsResponse>>(archivedListKey);

      movePrivateWorkspaceAgentSessionToLiveListCache(
        queryClient,
        variables.workspaceId,
        variables.sessionId,
        buildWorkspaceAgentSessionListCachePatch(new Date().toISOString(), { status: 'idle' }),
      );

      return {
        liveListKey,
        archivedListKey,
        previousLiveList,
        previousArchivedList,
      } satisfies UnarchiveMutationContext;
    },
    onError: (_error, variables, context) => {
      if (!context) {
        return;
      }

      const liveListParams = buildPrivateWorkspaceAgentSessionsListInfiniteParams(variables.workspaceId);
      removePrivateWorkspaceAgentSessionFromListCache(queryClient, liveListParams, variables.sessionId);

      if (context.previousArchivedList) {
        queryClient.setQueryData(context.archivedListKey, context.previousArchivedList);
      } else {
        void queryClient.invalidateQueries({ queryKey: context.archivedListKey });
      }

      queryClient.setQueryData(context.liveListKey, context.previousLiveList);
    },
    onSuccess: (unarchivedSession, variables) => {
      upsertPrivateWorkspaceAgentSessionInListCache(
        queryClient,
        buildPrivateWorkspaceAgentSessionsListInfiniteParams(variables.workspaceId),
        unarchivedSession,
      );

      queryClient.setQueryData(
        getPrivateWorkspaceAgentSessionGetKey({
          sessionId: variables.sessionId,
          workspaceId: variables.workspaceId,
        }),
        unarchivedSession,
      );
    },
  });
};
